import { type ImageType, TERMS_IMAGE, ImageSelector } from '~/components/image';
import { TwoColumnGrid } from '~/components/layout';
import { Description, Title } from '~/components/typography';
import { useCopy } from '~/hooks';
import { SelfAttestationForm } from '~/screens/vnm-terms/self-attestation-form/self-attestation-form';
import { FetchFunnelPacketResponse } from '~/services/fetch-funnel-packet';
import { FetchUtilityAccountResponse } from '~/services/fetch-utility-account';
import { BillingDataSource, ContractVersion, Utility } from '~/typings/context';

export const ReviewForm = ({ context, next, overrideCopy, loading, flags }: ReviewFormProps) => {
  const copy = useCopy(context, checkCopy, overrideCopy);

  // TO DO : Ideally this should be handled inside the wrapper component
  // This is a temporary fix as the overrideCopy is not currently passed to wrapper component

  const isSponsoredSolar =
    context.funnelPacket?.user?.originationSource?.originationSourceType === 'sponsored_solar';
  const defaultDescriptionLineOne = isSponsoredSolar
    ? copy.descriptionLineOneSponsoredSolar
    : copy.descriptionLineOne;
  const defaultDescriptionLineTwo = isSponsoredSolar
    ? copy.defaultDescriptionLineTwo
    : copy.descriptionLineTwo;

  return (
    <TwoColumnGrid testId="screen-Review-Form">
      <TwoColumnGrid.Primary>
        <Title fontWeight={700} fontSize="24px">
          {copy.title}
        </Title>
        {!!defaultDescriptionLineOne && (
          <Description mb={4}>{defaultDescriptionLineOne}</Description>
        )}
        {!!defaultDescriptionLineTwo && (
          <Description mb={4}>{defaultDescriptionLineTwo}</Description>
        )}
        <SelfAttestationForm
          context={context}
          flags={flags}
          copy={copy}
          next={next}
          loading={loading}
        />
      </TwoColumnGrid.Primary>
      <TwoColumnGrid.Secondary>
        <ImageSelector type={copy.imageType} />
      </TwoColumnGrid.Secondary>
    </TwoColumnGrid>
  );
};

export const checkCopy = () => {
  return defaultCopy();
};

export const defaultCopy = () => {
  return {
    title: 'Review your subscriber income verification form',
    defaultDescriptionLineOne:
      'To find out if you are eligible for additional savings, your state needs you to fill out an income status verification form. ',
    defaultDescriptionLineTwo:
      'Click “Review form” below and you will be taken to a new page that provides more details where you can self-attest to your household income level.',
    imageType: TERMS_IMAGE as ImageType,
    next: 'Review form',
    nonLmiNext: 'Continue',
    descriptionLineOneSponsoredSolar:
      'To find out if you are eligible for additional savings, please fill out an income status verification form. ',
    descriptionLineOneNJSelfAttestation:
      'To find out if you are eligible for additional savings, your state needs you to fill out an income status verification form.',
    descriptionLineTwoNJSelfAttestation:
      "First, select your county, household occupancy, and low-to-moderate income qualifying program (if applicable). Then click 'Review form' and you will be taken to a new page that requires your confirmation.",
    descriptionLineOneMDSelfAttestation:
      'You may qualify for additional savings if your household’s annual income falls below a certain threshold or if you participate in an approved income-eligible program.',
    descriptionLineOneVALmi:
      "You may qualify for additional solar savings if your household's annual income falls below a certain threshold, based on your household occupancy.",
    descriptionLineTwoVALmi:
      'To help us determine your eligibility, we need you to fill out a brief income status verification form.',
    descriptionLineTwo: '',
    descriptionLineOne: '',
    lmiSectionTitle: '',
    lmiSectionDescription: '',
    isLmi: {
      primary: 'Yes, I qualify as LMI',
      secondary:
        'If you choose this option, after clicking "Review form" below, you will see a form to self-attest your income status.',
    },
    maybeLmi: {
      primary: "I don't know if I qualify as LMI",
      secondary:
        'If you aren\'t sure, that\'s ok. After clicking "Review form" below, you will see a form to attest your income status. Go ahead and sign it. We will review your income level and check whether you qualify as LMI.',
    },
    notLmi: {
      primary: 'No, I do not qualify as LMI',
      secondary: "You're still guaranteed to save with community solar!",
    },
  };
};

export type ReviewFormCopy = ReturnType<typeof defaultCopy>;

export interface ReviewFormProps {
  context: ReviewFormContext;
  loading: boolean;
  next: (data) => void;
  flags?: {
    showSelfAttestationForm: boolean;
    showLmiOptions: boolean;
    requiresAddressFields: boolean;
    showCountySelect?: boolean;
    showHouseholdOccupancy?: boolean;
    showLmiProgram?: boolean;
    includeHouseIncome?: boolean;
  };
  overrideCopy?: (context: ReviewFormContext) => Partial<ReturnType<typeof defaultCopy>>;
}

export interface ReviewFormContext {
  contracts: ContractVersion[];
  billingSelection?: BillingDataSource;
  businessName?: string;
  email: string;
  funnelPacket: FetchFunnelPacketResponse;
  phoneNumber?: string | null;
  firstName: string;
  lastName: string;
  lmiStatus?: string;
  paymentMethod?: { lastFour: string; source: string };
  promoCode?: string; // used in solar landscape copy
  solarSavingsRate?: string;
  utility: Utility;
  isUtilityScraperEligible?: boolean;
  utilityAccount?: FetchUtilityAccountResponse['utilityAccount'];
}

export interface FetchUtilityScraperEligibleOptions {}
